import React, { useEffect, useState } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Button, Confirm, Icon } from 'semantic-ui-react';
import { toast, SemanticToastContainer } from 'react-semantic-toasts';
import { AddButton } from '../../components/AddButton/AddButton';
import { useUnitConversions } from '../../providers/UnitConversionsProvider';
import { UnitConversionModal } from './UnitConversionModals/UnitConversionModal';

const DEFAULT_SORT = [{ field: 'source_unit', dir: 'asc' }];
const PAGE_SIZE = 10;

export const UnitConversions = () => {
    const { unitConversions, total, getUnitConversions, deleteUnitConversion } = useUnitConversions();
    const [pageIndex, setPageIndex] = useState(0);
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState('Create');
    const [selectedConversion, setSelectedConversion] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [loading, setLoading] = useState(false);

    const refresh = () => {
        const activeSort = sort.length ? sort[0] : DEFAULT_SORT[0];
        getUnitConversions([{ fieldName: activeSort.field, dir: activeSort.dir }], pageIndex, PAGE_SIZE);
    };

    const handleModalSuccess = () => {
        refresh();
        toast({
            type: 'success',
            title: 'Success',
            description: modalType === 'Edit'
                ? 'Unit conversion updated successfully'
                : 'Unit conversion created successfully',
            animation: 'fade down',
            time: 4000,
        });
    };

    useEffect(() => {
        refresh();
    }, [getUnitConversions, pageIndex, sort]);

    const handleEdit = (dataItem) => {
        setSelectedConversion(dataItem);
        setModalType('Edit');
        setModalOpen(true);
    };

    const handleCreate = () => {
        setSelectedConversion(null);
        setModalType('Create');
        setModalOpen(true);
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await deleteUnitConversion(deleteTarget.source_unit, deleteTarget.target_unit);
            setDeleteTarget(null);
            refresh();
            toast({
                type: 'success',
                title: 'Success',
                description: 'Unit conversion deleted successfully',
                animation: 'fade down',
                time: 4000,
            });
        } catch (e) {
            setDeleteTarget(null);
            toast({
                type: 'error',
                description: e?.response?.data || 'An error occurred. Please try again.',
                animation: 'fade down',
                time: 5000,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="pageHeader">
                <h1>Unit Conversions</h1>
                <AddButton onClick={handleCreate}>Create Unit Conversion</AddButton>
            </div>
            <Grid
                data={unitConversions}
                total={total}
                pageable={true}
                sortable={true}
                sort={sort}
                onSortChange={e => { setSort(e.sort); setPageIndex(0); }}
                skip={pageIndex * PAGE_SIZE}
                take={PAGE_SIZE}
                onPageChange={e => setPageIndex(e.page.skip / e.page.take)}
            >
                <Column field="source_unit" title="Source Unit" cell={props => <td>{props.dataItem.source_unit}</td>} />
                <Column field="target_unit" title="Target Unit" cell={props => <td>{props.dataItem.target_unit}</td>} />
                <Column field="formula" title="Formula" cell={props => <td>{props.dataItem.formula}</td>} />
                <Column
                    sortable={false}
                    cell={props => (
                        <td>
                            <Button type="button" size="small" style={{ fontSize: '1rem' }} onClick={() => handleEdit(props.dataItem)}>
                                <Icon name="pencil" />Edit
                            </Button>
                            <Button type="button" size="small" style={{ fontSize: '1rem' }} onClick={() => setDeleteTarget(props.dataItem)}>
                                <Icon name="trash" />Delete
                            </Button>
                        </td>
                    )}
                />
            </Grid>
            <UnitConversionModal
                open={modalOpen}
                setOpen={setModalOpen}
                type={modalType}
                selectedConversion={selectedConversion}
                onSuccess={handleModalSuccess}
            />
            <SemanticToastContainer position="bottom-center" />
            <Confirm
                open={deleteTarget !== null}
                onCancel={() => !loading && setDeleteTarget(null)}
                onConfirm={handleDelete}
                content={`Delete conversion from "${deleteTarget?.source_unit}" to "${deleteTarget?.target_unit}"?`}
                confirmButton={<Button negative loading={loading} disabled={loading}>Delete</Button>}
                cancelButton={<Button disabled={loading}>Cancel</Button>}
            />
        </div>
    );
};
